import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  BookOpen, PenLine, Headphones, Mic, 
  Trophy, TrendingUp, Calendar, LogOut,
  ChevronRight, Settings, BarChart3, Crown, Bell, BellRing, Users, Menu, X
} from 'lucide-react';
import { isPushSupported, isPushSubscribed } from '@/utils/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    checkPremiumStatus();
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    try {
      if (isPushSupported()) {
        const subscribed = await isPushSubscribed();
        setHasNotifications(subscribed);
      }
    } catch (error) {
      console.error('Error checking notification status:', error);
    }
  };

  const checkPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/user-status`);
      setIsPremium(response.data.is_premium);
    } catch (error) {
      console.error('Error checking premium status:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, achievementsRes] = await Promise.all([
        axios.get(`${API}/users/stats`),
        axios.get(`${API}/users/achievements`)
      ]);
      setStats(statsRes.data);
      setAchievements(achievementsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setTimeout(() => navigate('/'), 100);
  };

  const sections = [
    { 
      id: 'reading', 
      title: 'Reading & Use of English', 
      titleEs: 'Lectura y Uso del Inglés',
      icon: <BookOpen className="w-6 h-6" />,
      color: 'bg-blue-500',
      path: '/reading'
    },
    { 
      id: 'writing', 
      title: 'Writing', 
      titleEs: 'Escritura',
      icon: <PenLine className="w-6 h-6" />,
      color: 'bg-green-500',
      path: '/writing'
    },
    { 
      id: 'listening', 
      title: 'Listening', 
      titleEs: 'Comprensión Auditiva',
      icon: <Headphones className="w-6 h-6" />,
      color: 'bg-purple-500',
      path: '/listening'
    },
    { 
      id: 'speaking', 
      title: 'Speaking', 
      titleEs: 'Expresión Oral',
      icon: <Mic className="w-6 h-6" />,
      color: 'bg-orange-500',
      path: '/speaking'
    }
  ];

  const unlockedAchievements = achievements.filter(a => a.unlocked);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_spanenglishprep/artifacts/i0n29hu3_Exam%20Ready%20English%20Logo.png" 
                alt="Exam Ready English" 
                className="h-8 w-auto" 
              />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-4">
              {isPremium && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1 text-xs font-semibold rounded-full">
                  <Crown className="w-3 h-3" />
                  Premium
                </span>
              )}
              {user?.level && (
                <span className={`level-badge level-${user.level.toLowerCase()}`}>
                  {user.level}
                </span>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/notifications')}
                data-testid="notifications-btn"
                className={hasNotifications ? 'text-orange-600' : 'text-slate-400'}
              >
                {hasNotifications ? (
                  <BellRing className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/profile')}
                data-testid="profile-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Perfil
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/affiliate')}
                data-testid="affiliate-btn"
                className="text-orange-600"
              >
                <Users className="w-4 h-4 mr-2" />
                Afiliados
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-slate-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              {isPremium && (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-full">
                  <Crown className="w-3 h-3" />
                </span>
              )}
              {user?.level && (
                <span className={`level-badge level-${user.level.toLowerCase()} text-xs px-2`}>
                  {user.level}
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 py-2 bg-white">
              <div className="flex flex-col gap-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { navigate('/notifications'); setMobileMenuOpen(false); }}
                  className={`justify-start ${hasNotifications ? 'text-orange-600' : 'text-slate-600'}`}
                >
                  {hasNotifications ? <BellRing className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
                  Notificaciones
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                  className="justify-start text-slate-600"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Perfil
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { navigate('/affiliate'); setMobileMenuOpen(false); }}
                  className="justify-start text-orange-600"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Afiliados
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="justify-start text-slate-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>
    
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-slate-900 tracking-tight">
            ¡Hola, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">
            {user?.level 
              ? `Preparando el nivel ${user.level}` 
              : 'Selecciona tu nivel para comenzar'}
          </p>
        </div>

        {/* Level Selection CTA if no level */}
        {!user?.level && (
          <div className="mb-8 p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-sm animate-fade-in-up">
            <h2 className="text-xl font-semibold mb-2">Selecciona tu nivel de Cambridge</h2>
            <p className="text-slate-300 mb-4">
              Elige el examen que estás preparando para personalizar tu experiencia
            </p>
            <Button 
              onClick={() => navigate('/level-select')}
              data-testid="select-level-btn"
              className="bg-orange-600 hover:bg-orange-500 text-white rounded-full"
            >
              Elegir Nivel
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Bento Grid */}
        <div className="grid grid-cols-4 gap-6">
          {/* Stats Overview - Spans 2 cols */}
          <div className="col-span-4 lg:col-span-2 row-span-2 bg-white border border-slate-200 p-6 animate-fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-orange-600" />
                Tu Progreso
              </h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/profile')}
                className="text-orange-600"
              >
                Ver todo
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-[#FDFBF7] rounded-sm">
                <p className="text-2xl font-bold text-slate-900">{stats?.completed_exercises || 0}</p>
                <p className="text-sm text-slate-600">Ejercicios</p>
              </div>
              <div className="p-4 bg-[#FDFBF7] rounded-sm">
                <p className="text-2xl font-bold text-slate-900">{stats?.average_score?.toFixed(0) || 0}%</p>
                <p className="text-sm text-slate-600">Promedio</p>
              </div>
            </div>

            <div className="space-y-4">
              {sections.map((section) => (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">{section.title}</span>
                    <span className="text-slate-900 font-medium">
                      {(stats?.section_progress?.[section.id] || 0).toFixed(0)}%
                    </span>
                  </div>
                  <Progress 
                    value={stats?.section_progress?.[section.id] || 0} 
                    className="h-2"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Section Cards */}
          {sections.map((section, index) => (
            <div 
              key={section.id}
              onClick={() => navigate(section.path)}
              className="col-span-2 lg:col-span-1 bg-white border border-slate-200 p-6 cursor-pointer hover:shadow-lg transition-all duration-300 group animate-fade-in-up"
              style={{ animationDelay: `${(index + 1) * 100}ms` }}
              data-testid={`section-${section.id}-card`}
            >
              <div className={`w-12 h-12 ${section.color} text-white rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                {section.icon}
              </div>
              <h3 className="font-semibold text-slate-900">{section.title}</h3>
              <p className="text-sm text-slate-500">{section.titleEs}</p>
              <div className="mt-4 flex items-center text-orange-600 text-sm font-medium">
                Practicar
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}

          {/* Achievements */}
          <div className="col-span-4 lg:col-span-2 bg-white border border-slate-200 p-6 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-orange-600" />
                Logros
              </h2>
              <span className="text-sm text-slate-500">
                {unlockedAchievements.length}/{achievements.length}
              </span>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {achievements.slice(0, 8).map((achievement) => (
                <div 
                  key={achievement.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${
                    achievement.unlocked 
                      ? 'bg-orange-50 border-orange-200' 
                      : 'bg-slate-50 border-slate-200 opacity-50'
                  }`}
                  title={achievement.unlocked ? achievement.name_es : `${achievement.name_es} (Bloqueado)`}
                >
                  <span className="text-xl">{achievement.icon}</span>
                  <span className="text-sm font-medium text-slate-700">
                    {achievement.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="col-span-4 lg:col-span-2 bg-slate-900 text-white p-6 animate-fade-in-up animation-delay-400">
            <h2 className="font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Continuar Practicando
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Mantén tu racha y mejora día a día
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={() => navigate('/reading')}
                className="bg-orange-600 hover:bg-orange-500 text-white rounded-full"
                data-testid="quick-reading-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Reading
              </Button>
              <Button 
                onClick={() => navigate('/speaking')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full"
                data-testid="quick-speaking-btn"
              >
                <Mic className="w-4 h-4 mr-2" />
                Speaking
              </Button>
            </div>
          </div>

          {/* Notification CTA */}
          {!hasNotifications && isPushSupported() && (
            <div 
              className="col-span-4 lg:col-span-2 bg-blue-50 border border-blue-200 p-6 cursor-pointer hover:shadow-md transition-all animate-fade-in-up animation-delay-500"
              onClick={() => navigate('/notifications')}
              data-testid="notification-cta-card"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Activa las notificaciones</h3>
                  <p className="text-slate-600 text-sm">
                    Recordatorios diarios para mantener tu racha
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          )}

          {/* Premium CTA */}
          {!isPremium && (
            <div 
              className={`${!hasNotifications && isPushSupported() ? 'col-span-4 lg:col-span-2' : 'col-span-4'} bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 cursor-pointer hover:shadow-lg transition-all animate-fade-in-up animation-delay-500`}
              onClick={() => navigate('/subscription')}
              data-testid="premium-cta-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Crown className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Desbloquea Premium</h3>
                    <p className="text-white/80 text-sm">
                      Audio real en Listening • Voice-to-Text en Speaking • Feedback ilimitado
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-6 h-6" />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
